// Centralized image guard for every upload (menu items, CFD ad, merchant QR,
// support-ticket attachments). Validates type, hard-rejects absurd files, and
// downscales/compresses large photos client-side BEFORE they hit Supabase
// Storage - so a huge phone photo still uploads, just smaller. Run every upload
// through prepareImageForUpload() so a giant file can never waste Storage
// (a billed resource) or slow the POS/CFD render.

// Target ceiling for what actually gets uploaded (after any downscale).
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB
// Refuse to even decode anything past this (avoids hanging the tab on a 50 MB file).
const HARD_REJECT_BYTES = 25 * 1024 * 1024; // 25 MB
// Longest edge after downscale - keeps the full-screen CFD ad crisp on 1080p
// displays while still shrinking giant phone photos. QR codes are small and
// stay untouched (they never exceed this).
const MAX_EDGE = 1920;
const JPEG_QUALITY = 0.85;

const fmtMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, width: img.naturalWidth, height: img.naturalHeight, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode failed"));
    };
    img.src = url;
  });

// Returns a File ready to upload (possibly the original, untouched, when it's
// already small enough). Throws a user-friendly Error for non-images or files
// too large to process. Empty/missing files pass straight through so callers
// keep their "no image -> default" behaviour.
export const prepareImageForUpload = async function (file) {
  if (!file || file.size === 0) return file;

  if (!file.type.startsWith("image/")) {
    throw new Error("That file is not an image. Please upload a PNG or JPG.");
  }
  if (file.size > HARD_REJECT_BYTES) {
    throw new Error(
      `That image is too large (${fmtMB(file.size)}). Please choose one under ${fmtMB(HARD_REJECT_BYTES)}.`,
    );
  }

  let loaded;
  try {
    loaded = await loadImage(file);
  } catch {
    throw new Error("Could not read that image - it may be corrupted. Try another file.");
  }

  const { img, width, height, url } = loaded;
  const longest = Math.max(width, height);
  const needsResize = longest > MAX_EDGE;
  const needsCompress = file.size > MAX_UPLOAD_BYTES;

  // Already within dimensions and under the ceiling: upload as-is. This keeps
  // small PNG logos / QR codes pixel-perfect (no needless re-encode).
  if (!needsResize && !needsCompress) {
    URL.revokeObjectURL(url);
    return file;
  }

  const scale = needsResize ? MAX_EDGE / longest : 1;
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(url);

  // Preserve PNG (keeps transparency for logos / QR); re-encode everything else
  // as JPEG, which is dramatically smaller for photos.
  const outType = file.type === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise((res) => canvas.toBlob(res, outType, JPEG_QUALITY));
  if (!blob) return file; // canvas unavailable - fall back to the original

  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `That image is still too large after compression (${fmtMB(blob.size)}). Please use a smaller one.`,
    );
  }

  const baseName = file.name.replace(/\.[^.]+$/, "");
  const ext = outType === "image/png" ? "png" : "jpg";
  return new File([blob], `${baseName}.${ext}`, { type: outType });
};
